import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  History,
  Refresh,
  Warning,
  Info,
  CheckCircle,
  ExpandMore,
  Timeline,
  Assessment,
  Settings
} from '@mui/icons-material';
import api from '../services/api';

const LogsPanel = ({ siteId, rootDomain }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bigqueryAvailable, setBigqueryAvailable] = useState(false);
  const [limit, setLimit] = useState(25);
  const [summaryDays, setSummaryDays] = useState(7);
  const [selectedLogTypes, setSelectedLogTypes] = useState([]);

  useEffect(() => {
    if (siteId) {
      fetchLogs();
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, limit, summaryDays, selectedLogTypes]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build query parameters
      let queryParams = `limit=${limit}`;
      if (selectedLogTypes.length > 0) {
        queryParams += `&log_types=${selectedLogTypes.join(',')}`;
      }
      
      const response = await api.get(`/sites/${siteId}/logs?${queryParams}`);
      
      if (response.data.success) {
        setLogs(response.data.logs);
        setBigqueryAvailable(response.data.bigquery_available);
      } else {
        setError('Failed to load logs');
        setBigqueryAvailable(response.data.bigquery_available);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      setError(`Failed to load logs: ${error.response?.data?.detail || error.message}`);
      setBigqueryAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get(`/sites/${siteId}/logs/summary?days=${summaryDays}`);
      
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching logs summary:', error);
    }
  };

  const getLogTypeIcon = (logType) => {
    const iconMap = {
      'login': <CheckCircle color="success" />,
      'add_page': <Info color="primary" />,
      'update_page': <Settings color="primary" />,
      'delete_page': <Warning color="warning" />,
      'plugin_activate': <CheckCircle color="success" />,
      'plugin_deactivate': <Warning color="warning" />
    };
    
    return iconMap[logType] || <Info color="default" />;
  };

  const getLogTypeColor = (logType) => {
    const colorMap = {
      'login': 'success',
      'add_page': 'primary',
      'update_page': 'info',
      'delete_page': 'warning',
      'plugin_activate': 'success',
      'plugin_deactivate': 'warning'
    };
    
    return colorMap[logType] || 'default';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };

  const formatLogType = (logType) => {
    return logType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleLogTypeFilter = (logType) => {
    setSelectedLogTypes(prev => {
      if (prev.includes(logType)) {
        // Remove from filter
        return prev.filter(type => type !== logType);
      } else {
        // Add to filter
        return [...prev, logType];
      }
    });
  };

  const clearAllFilters = () => {
    setSelectedLogTypes([]);
  };

  if (!bigqueryAvailable && !loading) {
    return (
      <Card>
        <CardHeader
          title="Site Activity Logs"
          avatar={<History color="primary" />}
        />
        <CardContent>
          <Alert severity="warning">
            BigQuery service is not available. Unable to load site logs.
            <br />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please ensure BigQuery credentials are properly configured and the logs dataset is accessible.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Summary Statistics */}
      {summary.total_logs > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assessment sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Activity Summary (Last {summaryDays} days)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {summary.total_logs}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Events
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={9}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Event Types
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(summary.log_types || {}).map(([logType, count]) => (
                      <Chip
                        key={logType}
                        label={`${formatLogType(logType)}: ${count}`}
                        color={getLogTypeColor(logType)}
                        variant={selectedLogTypes.includes(logType) ? "filled" : "outlined"}
                        size="small"
                        clickable
                        onClick={() => handleLogTypeFilter(logType)}
                        sx={{
                          '&:hover': {
                            backgroundColor: selectedLogTypes.includes(logType) 
                              ? 'rgba(0, 0, 0, 0.1)' 
                              : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Controls */}
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Recent Activity Logs"
          avatar={<Timeline color="primary" />}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <InputLabel>Days</InputLabel>
                <Select
                  value={summaryDays}
                  label="Days"
                  onChange={(e) => setSummaryDays(e.target.value)}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={7}>7</MenuItem>
                  <MenuItem value={14}>14</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <InputLabel>Limit</InputLabel>
                <Select
                  value={limit}
                  label="Limit"
                  onChange={(e) => setLimit(e.target.value)}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              {selectedLogTypes.length > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={clearAllFilters}
                  size="small"
                >
                  Clear Filters ({selectedLogTypes.length})
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
                onClick={() => {
                  fetchLogs();
                  fetchSummary();
                }}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          }
        />
        <CardContent>
          {selectedLogTypes.length > 0 && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                Active Filters:
              </Typography>
              {selectedLogTypes.map(logType => (
                <Chip
                  key={logType}
                  label={formatLogType(logType)}
                  color={getLogTypeColor(logType)}
                  size="small"
                  onDelete={() => handleLogTypeFilter(logType)}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Alert severity="info">
              No logs found for {rootDomain}. Activity will appear here once the site starts generating events.
            </Alert>
          ) : (
            <List>
              {logs.map((log, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {getLogTypeIcon(log.logType)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" component="span">
                            {log.description}
                          </Typography>
                          <Chip
                            label={formatLogType(log.logType)}
                            color={getLogTypeColor(log.logType)}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" component="div">
                            <strong>User:</strong> {log.changeByName} ({log.changeByEmail})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="div">
                            <strong>Time:</strong> {formatTimestamp(log.timestamp)}
                          </Typography>
                          {log.urlUpdated && log.urlUpdated !== 'N/A' && (
                            <Typography variant="body2" color="text.secondary" component="div">
                              <strong>URL:</strong> 
                              <Button
                                size="small"
                                href={log.urlUpdated}
                                target="_blank"
                                sx={{ ml: 1, minWidth: 'auto', p: 0.5 }}
                              >
                                {log.urlUpdated.length > 50 ? 
                                  log.urlUpdated.substring(0, 50) + '...' : 
                                  log.urlUpdated
                                }
                              </Button>
                            </Typography>
                          )}
                          {log.changeType && log.changeType !== 'N/A' && (
                            <Typography variant="body2" color="text.secondary" component="div">
                              <strong>Change Type:</strong> {log.changeType}
                            </Typography>
                          )}
                          {log.pluginVersion && (
                            <Typography variant="body2" color="text.secondary" component="div">
                              <strong>Plugin Version:</strong> {log.pluginVersion}
                            </Typography>
                          )}
                          {log.additionalData && Object.keys(log.additionalData).length > 0 && (
                            <Accordion sx={{ mt: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="caption">Additional Details</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(log.additionalData, null, 2)}
                                </pre>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < logs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LogsPanel;